import theme, { newColors } from 'artkive/utils/theme'

const FONT = '"Montserrat", sans-serif'
const TEXT_COLOR = theme.colors.black

// Reviews.io Polaris widget for /reviews: the combined company + third-party feed, highest rated first so the page
// doesn't open on whichever review came in last (visitors can still sort by Latest). The widget's own
// header is off because it only counts company reviews and would contradict the page's aggregate line; its CSS
// variables are mapped to the site theme.
export default {
  store: 'artkiveapp.com',
  widget: 'polaris',
  options: {
    types: 'combined_store_third_party',
    enable_sentiment_analysis: false,
    lang: 'en',
    layout: 'bordered',
    per_page: 30,
    combined_store_third_party: {
      hide_if_no_results: false,
    },
    header: {
      enable_summary: false,
      enable_ratings: false,
      enable_attributes: false,
      enable_image_gallery: true,
      enable_percent_recommended: false,
      enable_write_review: false,
      enable_ask_question: false,
      enable_sub_header: true,
      rating_decimal_places: 1,
      use_write_review_button: false,
      enable_if_no_results: false,
      show_review_title_field: false,
    },
    sentiment: {
      badge_text: 'Our Customers Say',
    },
    // Sort-only controls: Polaris ships Latest / Oldest / Most Popular / Highest Rated and none of them can be removed
    // from the embed; a "Lowest Rated" entry would have to come from the Reviews.io dashboard.
    filtering: {
      enable: true,
      enable_text_search: true,
      enable_sorting: true,
      enable_product_filter: false,
      enable_media_filter: false,
      enable_overall_rating_filter: false,
      enable_language_filter: false,
      enable_language_filter_language_change: false,
      enable_ratings_filters: false,
      enable_attributes_filters: false,
      enable_expanded_filters: false,
    },
    reviews: {
      default_sort: 'highest_rating',
      enable_avatar: true,
      enable_reviewer_name: true,
      enable_reviewer_address: true,
      reviewer_address_format: 'city, country',
      enable_verified_badge: true,
      enable_subscriber_badge: true,
      review_content_filter: 'undefined',
      enable_reviewer_recommends: true,
      enable_attributes: true,
      enable_product_name: true,
      enable_link_product: false,
      enable_replies: true,
      enable_images: true,
      enable_ratings: true,
      enable_share: false,
      enable_helpful_vote: true,
      enable_helpful_display: true,
      enable_report: true,
      enable_date: true,
      date_format: 'undefined',
      date_location: 'undefined',
      enable_third_party_source: true,
      combine_title_stars: true,
      show_untranslated: false,
    },
  },
  translations: {
    // The widget's empty-search message, which otherwise says the company has no reviews at all.
    'No reviews collected for this company yet': 'No reviews match your search',
    'Verified Customer': 'Verified Customer',
    'Newest First': 'Latest',
    'Oldest First': 'Oldest',
    'Highest Rating': 'Highest Rated',
    'Lowest Rating': 'Lowest Rated',
  },
  styles: {
    '--base-font-size': '16px',

    '--common-button-font-family': FONT,
    '--common-button-font-size': '16px',
    '--common-button-font-weight': '600',
    '--common-button-letter-spacing': '0',
    '--common-button-text-transform': 'none',
    '--common-button-vertical-padding': '10px',
    '--common-button-horizontal-padding': '20px',
    '--common-button-border-width': '1px',
    '--common-button-border-radius': '8px',

    '--primary-button-bg-color': newColors.sky[700],
    '--primary-button-border-color': `${newColors.sky[700]}00`,
    '--primary-button-text-color': newColors.white,

    '--secondary-button-bg-color': newColors.white,
    '--secondary-button-border-color': newColors.sky[500],
    '--secondary-button-text-color': newColors.sky[700],

    '--common-star-color': theme.colors.yellow.dark,
    '--common-star-disabled-color': newColors.grey[300],
    '--medium-star-size': '24px',
    '--small-star-size': '22px',

    '--heading-text-color': TEXT_COLOR,
    '--heading-text-font-weight': '600',
    '--heading-text-font-family': FONT,
    '--heading-text-line-height': '1.4',
    '--heading-text-letter-spacing': '0',
    '--heading-text-transform': 'none',

    '--body-text-color': newColors.grey[700],
    '--body-text-font-weight': '500',
    '--body-text-font-family': FONT,
    '--body-text-line-height': '1.6',
    '--body-text-letter-spacing': '0',
    '--body-text-transform': 'none',

    '--inputfield-text-font-family': FONT,
    '--input-text-font-size': '16px',
    '--inputfield-text-font-weight': '500',
    '--inputfield-text-color': TEXT_COLOR,
    '--inputfield-border-color': newColors.sky[300],
    '--inputfield-background-color': newColors.white,
    '--inputfield-border-width': '1px',
    '--inputfield-border-radius': '8px',

    '--common-border-color': newColors.sky[300],
    '--common-border-width': '1px',
    '--common-sidebar-width': '200px',

    '--filters-panel-bg-color': 'transparent',
    '--filters-panel-font-size': '16px',
    '--filters-panel-horizontal-padding': '0',
    '--filters-panel-vertical-padding': '0',

    '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
    '--slider-indicator-button-color': TEXT_COLOR,
    '--slider-indicator-width': '190px',

    '--badge-icon-color': newColors.sky[700],
    '--badge-icon-font-size': 'inherit',
    '--badge-text-color': newColors.grey[700],
    '--badge-text-font-size': 'inherit',
    '--badge-text-letter-spacing': 'inherit',
    '--badge-text-transform': 'inherit',

    '--author-font-size': '20px',
    '--author-text-transform': 'none',

    '--avatar-thumbnail-size': '48px',
    '--avatar-thumbnail-border-radius': '8px',
    '--avatar-thumbnail-text-color': newColors.sky[900],
    '--avatar-thumbnail-bg-color': newColors.sky[300],

    '--photo-video-thumbnail-size': '80px',
    '--photo-video-thumbnail-border-radius': '6px',

    '--mediaslider-scroll-button-icon-color': newColors.grey[700],
    '--mediaslider-scroll-button-bg-color': 'rgba(255, 255, 255, 0.85)',
    '--mediaslider-overlay-text-color': newColors.white,
    '--mediaslider-overlay-bg-color': 'rgba(0, 0, 0, 0.8)',
    '--mediaslider-item-size': '110px',

    '--pagination-tab-text-color': TEXT_COLOR,
    '--pagination-tab-text-transform': 'none',
    '--pagination-tab-text-letter-spacing': '0',
    '--pagination-tab-text-font-size': '16px',
    '--pagination-tab-text-font-weight': '600',

    '--pagination-tab-active-text-color': TEXT_COLOR,
    '--pagination-tab-active-text-font-weight': '600',
    '--pagination-tab-active-border-color': newColors.sky[700],
    '--pagination-tab-border-width': '2px',
  },
}
